import React, { useState } from "react";
import { createRootRoute, createRouter, lazyRouteComponent, Outlet } from "@tanstack/react-router";
import { NuqsAdapter } from "nuqs/adapters/tanstack-router";
import { AppProviders } from "@/widgets/app-shell/ui/AppProviders";
import { registerTokenRefresher } from "@/shared/api/client";
import { authService } from "@/entities/session/api/authService";
import { createTenantRouting, stripTenantBase, TenantRoutingProvider, tenantBasepath } from "@/shared/lib/router/tenantPath";
import { consoleUrlForPlatformPath, isConsoleEnabled, isConsoleHost } from "@/shared/config/consoleHost";
import { createPageRoutes, FlushContext, loadNotFoundPage, pageRoots } from "./routes";
import { renderRouter, routerSearchOptions } from "./render";
import { scheduleSentry } from "./sentry";
import "@/shared/i18n";
// Fontes self-hosted (sem round-trip ao Google Fonts no caminho crítico).
import "@fontsource-variable/inter";
import "@fontsource-variable/jetbrains-mono";
import "@/global/globals.css";
import "@/entities/project-planning/ui/gantt/zegantt.css";

// A organização vem do 1º segmento da URL e vira a base de todas as rotas.
const tenantRouting = createTenantRouting(window.location.pathname, pageRoots);

function RootComponent() {
  const [flush, setFlush] = useState(false);
  return (
    <NuqsAdapter>
      <TenantRoutingProvider value={tenantRouting}>
        <AppProviders flush={flush}>
          <FlushContext.Provider value={setFlush}>
            <Outlet />
          </FlushContext.Provider>
        </AppProviders>
      </TenantRoutingProvider>
    </NuqsAdapter>
  );
}

function mountApp() {
  const rootRoute = createRootRoute({
    component: RootComponent,
    notFoundComponent: lazyRouteComponent(loadNotFoundPage),
  });

  // O basepath só é lido na criação do router: ir para outra base, mesmo a da organização
  // atual vinda de um endereço sem slug, exige recarregar a página.
  renderRouter(
    createRouter({
      basepath: tenantBasepath(tenantRouting.slug),
      routeTree: rootRoute.addChildren(createPageRoutes(rootRoute)),
      ...routerSearchOptions,
    }),
  );
}

registerTokenRefresher((refreshToken) => authService.refreshToken(refreshToken));

/** Com o console ligado, /platform, o centro de entregas e os erros do app (com ou sem slug) passam a abrir lá. */
function platformRedirect(): string | null {
  if (!isConsoleEnabled()) return null;
  const { pathname, search, hash } = window.location;
  return consoleUrlForPlatformPath(`${stripTenantBase(pathname, tenantRouting.slug)}${search}${hash}`);
}

// O console da plataforma é o mesmo build noutro host; a árvore dele só é baixada lá.
const redirectToConsole = isConsoleHost() ? null : platformRedirect();
if (isConsoleHost()) {
  void import("./console").then(({ mountConsole }) => mountConsole());
} else if (redirectToConsole) {
  window.location.replace(redirectToConsole);
} else {
  mountApp();
}

scheduleSentry();
